import { useEffect, useMemo, useState } from 'react';
import { isFinite, merge } from 'lodash';
import clsx from 'clsx';
import { Backdrop, CircularProgress } from '@material-ui/core';
import { ErrorOutline } from '@material-ui/icons';
import { t, Trans } from '@lingui/macro';

import useStyles from './ReportDialogStyles';
import { useReportService, buildReportOptions } from '../../../services/ReportService';
import { optionsType } from './ReportOptions';
import Banner from '../../banner/Banner';
import { getLogger } from '../../../utilities/UseLog';

const log = getLogger('ReportPreview');

const MAX_PREVIEW_TABLE_ROWS = 250;

export default function ReportPreview({ options }) {
    const classes = useStyles();
    const reportService = useReportService();
    const [reportUrl, setReportUrl] = useState(null);
    const [reportError, setReportError] = useState(false);
    const reportOptions = useMemo(() => {
        if (isFinite(options?.pdf?.maxTableRows)) {
            return merge({}, options, {
                pdf: {
                    // Limit the table rows in previews to avoid slow reports.
                    maxTableRows: Math.min(MAX_PREVIEW_TABLE_ROWS, options.pdf.maxTableRows),
                },
            });
        }

        return options;
    }, [options]);
    // We have to check if the value is finite (i.e. not NaN) because NaN !== NaN evaluates to true.
    const [showTableRowsBanner, setShowTableRowsBanner] = useState(
        isFinite(options?.pdf?.maxTableRows) && options?.pdf?.maxTableRows !== reportOptions?.pdf?.maxTableRows
    );

    useEffect(() => {
        reportService
            .createPdf(buildReportOptions(reportOptions, true))
            .then((response) => {
                // shout out to https://stackoverflow.com/questions/2104608/hiding-the-toolbars-surrounding-an-embedded-pdf
                // for showing that we can disable toolbars and stuff for some browsers
                const pdfUrl = `${reportService.getPdfUrl(response, true)}#toolbar=0&navpanes=0&scrollbar=0`;
                setReportUrl(pdfUrl);
            })
            .catch((error) => {
                log.error(error);
                setReportError(true);
            });
    }, [reportService, reportOptions]);

    return (
        <div className={clsx(classes.reportPreview, 'scroll-container')}>
            {showTableRowsBanner && (
                <Banner
                    type="info"
                    text={t`Report previews display a maximum of ${MAX_PREVIEW_TABLE_ROWS} table rows`}
                    onRemove={() => setShowTableRowsBanner(false)}
                />
            )}
            {reportUrl ? (
                <div className={classes.reportPreviewImageContainer}>
                    <img alt="Report Preview" src={reportUrl} className={classes.reportPreviewImage} />
                </div>
            ) : (
                <Backdrop className={classes.reportPreviewBackdrop} open invisible={reportError}>
                    {reportError ? (
                        <div className={classes.reportPreviewError}>
                            <ErrorOutline />
                            <span>
                                <Trans>An error occurred while generating the report preview. Please try again.</Trans>
                            </span>
                        </div>
                    ) : (
                        <CircularProgress />
                    )}
                </Backdrop>
            )}
        </div>
    );
}

ReportPreview.propTypes = {
    options: optionsType.isRequired,
};
